import { computed } from 'vue'
import { useAuth, useUser, useClerk, useSession } from '@clerk/vue'
import { isDevMode, useDevLinkflareAuth, useAnonymousLinkflareAuth } from './devAuth.js'
import {
  areSessionTokensSuspended,
  suspendSessionTokens,
  sessionNeedsReverification,
  openReverification
} from '../utils/accountDeletion.js'

export function useLinkflareAuth() {
  // Dev mode: skip Clerk entirely, return mock auth state
  if (isDevMode) {
    return useDevLinkflareAuth()
  }

  // Defensive fallback: if the Clerk Vue plugin is not installed in the
  // current app (e.g. the embed bundle `embed-main.js` deliberately omits
  // it), `useAuth()` throws "@clerk/vue: useAuth can only be used when the
  // Vue plugin is installed". Catch that and degrade to an anonymous,
  // unauthenticated auth state instead of crashing the component setup.
  let clerkHooks
  try {
    clerkHooks = {
      auth: useAuth(),
      user: useUser(),
      clerk: useClerk(),
      session: useSession(),
    }
  } catch (e) {
    return useAnonymousLinkflareAuth()
  }

  const { isLoaded, isSignedIn } = clerkHooks.auth
  const { user } = clerkHooks.user
  const clerk = clerkHooks.clerk
  const { session } = clerkHooks.session

  const isAuthenticated = computed(() => isLoaded.value && isSignedIn.value)
  const isLoading = computed(() => !isLoaded.value)
  
  const userDisplayName = computed(() => {
    if (!user.value) return 'User'
    const firstName = user.value.firstName || ''
    const lastName = user.value.lastName || ''
    const fullName = `${firstName} ${lastName}`.trim()
    return fullName || 'User'
  })

  const userEmail = computed(() => {
    if (!user.value) return ''
    return user.value.emailAddresses?.[0]?.emailAddress || ''
  })

  const getAuthToken = async () => {
    try {
      
      if (!isLoaded.value || !isSignedIn.value) {
        return null
      }
      
      if (!clerk.value) {
        console.error('Clerk instance not available')
        return null
      }

      // The account behind this session was just deleted: a token here would let the
      // backend register it again. See utils/accountDeletion.js.
      if (areSessionTokensSuspended((session.value || clerk.value.session)?.id)) {
        return null
      }

      // Try to get token from session hook first
      if (session.value) {
        return await session.value.getToken()
      }
      
      // Fallback to clerk instance
      const clerkSession = clerk.value.session
      if (!clerkSession) {
        console.error('No active session')
        return null
      }
      
      return await clerkSession.getToken()
    } catch (error) {
      console.error('Error getting auth token:', error)
      return null
    }
  }

  const handleSignOut = async () => {
    try {
      
      if (!isLoaded.value) {
        console.error('Auth not loaded yet')
        return false
      }
      
      if (!clerk.value) {
        console.error('Clerk instance not available')
        return false
      }
      
      
      // Use clerk instance to sign out with redirect
      await clerk.value.signOut({
        redirectUrl: '/sign-in'
      })
      return true
    } catch (error) {
      console.error('Error signing out:', error)
      return false
    }
  }

  // Clerk-side steps for utils/accountDeletion.js deleteAccountEverywhere.
  const accountDeletion = {
    needsReverification: () => sessionNeedsReverification(clerk.value?.session || session.value),
    reverify: () => openReverification(clerk.value),
    deleteClerkUser: () => user.value.delete(),
    suspendTokens: () => suspendSessionTokens((clerk.value?.session || session.value)?.id),
    signOut: async (redirectUrl) => {
      try {
        await clerk.value.signOut({ redirectUrl })
      } finally {
        // Clerk navigates on its own; this covers a signOut that threw or stayed on the page
        window.location.assign(redirectUrl)
      }
    }
  }

  return {
    // States
    isLoaded,
    isSignedIn,
    isAuthenticated,
    isLoading,
    user,
    userDisplayName,
    userEmail,

    // Methods
    getAuthToken,
    handleSignOut,
    accountDeletion
  }
}
